import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Button, Form, Input, Space, Tag, Popconfirm, notification } from 'antd';
import bcrypt from 'bcryptjs';
import { PlusOutlined, EditOutlined, DeleteOutlined, IdcardOutlined, SearchOutlined } from '@ant-design/icons';
import { database, ref, onValue, set, remove } from '../../Auth/db/firebase';
import DataTable from '../../../components/shared/DataTable';
import FormModal from '../../../components/shared/FormModal';

const Cashiers = () => {
	const { facilityId } = useParams();
	const [list, setList] = useState([]);
	const [loading, setLoading] = useState(true);
	const [search, setSearch] = useState('');
	const [viewMode, setViewMode] = useState('table');
	const [modalOpen, setModalOpen] = useState(false);
	const [editing, setEditing] = useState(null);
	const [form] = Form.useForm();
	const [api, contextHolder] = notification.useNotification();

	useEffect(() => {
		const cashiersRef = ref(database, `cashiers/${facilityId}`);
		const unsub = onValue(cashiersRef, (snap) => {
			const arr = [];
			if (snap.exists()) {
				const data = snap.val();
				Object.keys(data).forEach((id) => arr.push({ key: id, id, ...data[id] }));
			}
			setList(arr.sort((a,b)=> (b.createdAt||'').localeCompare(a.createdAt||'')));
			setLoading(false);
		});
		return () => unsub();
	}, [facilityId]);

	const openAdd = () => {
		setEditing(null);
		form.resetFields();
		setModalOpen(true);
	};

	const openEdit = (record) => {
		setEditing(record);
		form.setFieldsValue({ name: record.name, email: record.email, phone: record.phone, post: record.post });
		setModalOpen(true);
	};

	const generateId = (name) => {
		const fac = (facilityId || '').replace(/[^a-zA-Z]/g,'').toUpperCase().slice(0,3).padEnd(3,'X');
		const now = new Date();
		const mm = String(now.getMonth()+1).padStart(2,'0');
		const yy = String(now.getFullYear()).slice(-2);
		const n3 = (name||'').replace(/[^a-zA-Z]/g,'').toUpperCase().slice(0,3).padEnd(3,'X');
		const prefix = `${fac}${mm}${n3}${yy}`; // e.g. MAR09DAV25
		// find next sequence
		const seq = list
			.filter(c => c.id && c.id.startsWith(prefix))
			.map(c => parseInt(c.id.slice(prefix.length),10))
			.filter(n => !isNaN(n));
		const next = String((seq.length ? Math.max(...seq) : 0) + 1).padStart(2,'0');
		return `${prefix}${next}`; // e.g. MAR09DAV2501
	};

	const handleSubmit = async (values) => {
		try {
			const now = new Date().toISOString();
			const rawEmail = values.email?.trim() || '';
			const normalizedEmail = rawEmail.toLowerCase();
			if (editing) {
				await set(ref(database, `cashiers/${facilityId}/${editing.id}`), {
					...editing,
					name: values.name.trim(),
					email: normalizedEmail,
					phone: values.phone?.trim() || '',
					post: values.post?.trim() || '',
					updatedAt: now
				});
				api.success({ message: 'Cashier updated' });
			} else {
				const id = generateId(values.name);
				const passwordHash = await bcrypt.hash(id, 10);
				await set(ref(database, `cashiers/${facilityId}/${id}`), {
					name: values.name.trim(),
					email: normalizedEmail,
					phone: values.phone?.trim() || '',
					post: values.post?.trim() || '',
					id,
					createdAt: now,
					active: true,
					passwordHash,
					passwordUpdatedAt: now
				});
				api.success({ message: `Cashier added. ID: ${id}` });
			}
			setModalOpen(false);
			form.resetFields();
		} catch (e) {
			api.error({ message: 'Save failed', description: e.message });
		}
	};

	const handleDelete = async (record) => {
		try {
			await remove(ref(database, `cashiers/${facilityId}/${record.id}`));
			api.success({ message: 'Cashier deleted' });
		} catch (e) {
			api.error({ message: 'Delete failed', description: e.message });
		}
	};

	const columns = [
		{ title: 'ID', dataIndex: 'id', key: 'id', render: (t)=> <Tag color='blue'>{t}</Tag> },
		{ title: 'Name', dataIndex: 'name', key: 'name' },
		{ title: 'Email', dataIndex: 'email', key: 'email' },
		{ title: 'Phone', dataIndex: 'phone', key: 'phone' },
		{ title: 'Post', dataIndex: 'post', key: 'post', render: (t)=> t ? <Tag>{t}</Tag> : <span>-</span> },
		{ title: 'Created', dataIndex: 'createdAt', key: 'createdAt', render: (t)=> t ? new Date(t).toLocaleString() : '-' },
		{
			title: 'Actions', key: 'actions', width: 140,
			render: (_, record) => (
				<Space>
					<Button size='small' icon={<EditOutlined />} onClick={() => openEdit(record)} />
					<Popconfirm title='Delete cashier?' onConfirm={() => handleDelete(record)}>
						<Button size='small' danger icon={<DeleteOutlined />} />
					</Popconfirm>
				</Space>
			)
		}
	];

	const filtered = useMemo(() => list.filter(c => {
		if (!search) return true;
		const s = search.toLowerCase();
		return (
			c.name?.toLowerCase().includes(s) ||
			c.email?.toLowerCase().includes(s) ||
			c.phone?.toLowerCase().includes(s) ||
			c.id?.toLowerCase().includes(s)
		);
	}), [list, search]);

	const renderCard = (c) => (
		<Card key={c.id} size='small' hoverable>
			<Space direction='vertical' style={{ width: '100%' }}>
				<Space>
					<IdcardOutlined /> <strong>{c.name}</strong>
				</Space>
				<div><Tag color='blue'>{c.id}</Tag></div>
				{c.post && <Tag>{c.post}</Tag>}
				<div style={{ display: 'flex', justifyContent: 'space-between' }}>
					<span>{c.email || '-'}</span>
					<span>{c.phone || '-'}</span>
				</div>
				<Space>
					<Button size='small' icon={<EditOutlined />} onClick={() => openEdit(c)}>Edit</Button>
					<Popconfirm title='Delete cashier?' onConfirm={() => handleDelete(c)}>
						<Button size='small' danger icon={<DeleteOutlined />}>Delete</Button>
					</Popconfirm>
				</Space>
			</Space>
		</Card>
	);

	return (
		<div style={{ marginTop: 'var(--spacing-lg)' }}>
			{contextHolder}
			<Card
				title={<span style={{ fontSize: '1.25rem', fontWeight: 600 }}>Cashiers</span>}
				extra={
					<Space wrap>
						<Input 
							placeholder='Search cashiers...' 
							allowClear 
							value={search} 
							onChange={(e)=> setSearch(e.target.value)} 
							style={{ 
								width: 250,
								minWidth: 180,
								borderRadius: 'var(--radius-lg)'
							}}
							prefix={<SearchOutlined style={{ color: 'var(--text-secondary)' }} />}
						/>
						<Button type='primary' icon={<PlusOutlined />} onClick={openAdd}>Add Cashier</Button>
					</Space>
				}
			>
				<DataTable
					data={filtered}
					columns={columns}
					viewMode={viewMode}
					onViewModeChange={setViewMode}
					renderCard={renderCard}
					loading={loading}
					tableProps={{ pagination: { pageSize: 8 } }}
					gridProps={{ style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 } }}
				/>
			</Card>

			<FormModal
				title={editing ? 'Edit Cashier' : 'Add Cashier'}
				open={modalOpen}
				onCancel={() => { setModalOpen(false); form.resetFields(); }}
				onSubmit={handleSubmit}
				form={form}
				okText={editing ? 'Update' : 'Create'}
				width={520}
			>
				<Form.Item name='name' rules={[{ required: true, message: 'Enter name' }]}>
					<Input placeholder='Full name' />
				</Form.Item>
				<Form.Item name='email'>
					<Input placeholder='Email' />
				</Form.Item>
				<Form.Item name='phone'>
					<Input placeholder='Telephone' />
				</Form.Item>
				<Form.Item name='post'>
					<Input placeholder='Post (optional)' />
				</Form.Item>
				{!editing && (
					<div className='info-alert' style={{ fontSize: 12 }}>
						ID will be generated on save e.g. FAC09NAM25xx
					</div>
				)}
			</FormModal>
		</div>
	);
};

export default Cashiers;

